import { memo } from "react";
import { isValid } from "date-fns";
import { ActorType } from "@common/types/auditLog";
import { pluralizeWithCount } from "@common/helpers/string";
import { Entity } from "@common/types/entity";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { AuditLogActor } from "@components/auditLog/AuditLogActor";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { DocumentName } from "@components/documents/DocumentName";
import { formatDateForTransaction } from "@helpers/date";
import { VStack } from "@design-system/VStack/VStack";
import dateStyles from "../../../cells/HistoryDetailsCells.module.scss";
import { isRecord } from "../../../utils/historyDetailsHelpers";
import { HistoryTaskUpdateDetailsRow } from "../historyTaskUpdateDetailsViewConfig";

interface CellProps {
    row: { original: HistoryTaskUpdateDetailsRow };
}

const renderDateLabel = (value: unknown): string => {
    if (value === null || value === undefined) {
        return "-";
    }
    const parsedDate = value instanceof Date ? value : new Date(String(value));
    if (!isValid(parsedDate)) {
        return "-";
    }
    return formatDateForTransaction(parsedDate);
};

const toActorId = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string" && value.length > 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

type EntityForIcon = Pick<
    Entity,
    "id" | "name" | "iconColor" | "workspaceId" | "logo"
>;

const isEntityForIcon = (value: unknown): value is EntityForIcon => {
    if (!isRecord(value)) {
        return false;
    }
    return (
        typeof value.id === "number" &&
        typeof value.name === "string" &&
        typeof value.workspaceId === "string"
    );
};

const toEntities = (value: unknown): EntityForIcon[] => {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isEntityForIcon);
};

const toDisplayText = (value: unknown): string => {
    if (typeof value === "string") {
        return value;
    }
    if (value === null || value === undefined) {
        return "";
    }
    return String(value);
};

export const PreviousDateCell = memo(({ row }: CellProps) => (
    <ViewTableCell className={dateStyles.dateCell}>
        {renderDateLabel(row.original.previousValue)}
    </ViewTableCell>
));
PreviousDateCell.displayName = "PreviousDateCell";

export const ModifiedDateCell = memo(({ row }: CellProps) => (
    <ViewTableCell className={dateStyles.dateCell}>
        {renderDateLabel(row.original.modifiedValue)}
    </ViewTableCell>
));
ModifiedDateCell.displayName = "ModifiedDateCell";

const ActorCell = ({ id, name }: { id: unknown; name: string }) => {
    const actorId = toActorId(id);
    if (actorId === null) {
        return <ViewTableCell>-</ViewTableCell>;
    }
    return (
        <ViewTableCell>
            <AuditLogActor
                actorId={actorId}
                actorName={name.length > 0 ? name : null}
                actorType={ActorType.USER}
            />
        </ViewTableCell>
    );
};

export const PreviousActorCell = memo(({ row }: CellProps) => (
    <ActorCell id={row.original.previousValue} name={row.original.previous} />
));
PreviousActorCell.displayName = "PreviousActorCell";

export const ModifiedActorCell = memo(({ row }: CellProps) => (
    <ActorCell id={row.original.modifiedValue} name={row.original.modified} />
));
ModifiedActorCell.displayName = "ModifiedActorCell";

const EntityListCell = ({ value }: { value: unknown }) => {
    const entities = toEntities(value);
    if (entities.length === 0) {
        return <ViewTableCell>-</ViewTableCell>;
    }
    return (
        <ViewTableCell>
            <EntityAvatarGroup
                entities={entities}
                max={3}
                size="xs"
                label={pluralizeWithCount("Entity", entities.length)}
            />
        </ViewTableCell>
    );
};

export const PreviousEntityListCell = memo(({ row }: CellProps) => (
    <EntityListCell value={row.original.previousValue} />
));
PreviousEntityListCell.displayName = "PreviousEntityListCell";

export const ModifiedEntityListCell = memo(({ row }: CellProps) => (
    <EntityListCell value={row.original.modifiedValue} />
));
ModifiedEntityListCell.displayName = "ModifiedEntityListCell";

interface DocumentItem {
    id?: number | string;
    fileName?: string;
}

const toDocumentItems = (value: unknown): DocumentItem[] => {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((item): DocumentItem[] => {
        if (!isRecord(item)) {
            return [];
        }
        let fileName: string | null = null;
        if (typeof item.fileName === "string") {
            fileName = item.fileName;
        } else if (typeof item.name === "string") {
            fileName = item.name;
        }
        if (!fileName) {
            return [];
        }
        const id =
            typeof item.id === "number" || typeof item.id === "string"
                ? item.id
                : undefined;
        return [{ id, fileName }];
    });
};

const DocumentListCell = ({ value }: { value: unknown }) => {
    const documents = toDocumentItems(value);
    if (documents.length === 0) {
        return <ViewTableCell>-</ViewTableCell>;
    }
    return (
        <ViewTableCell>
            <VStack gap={4}>
                {documents.map((doc, index) => (
                    <DocumentName
                        key={`${doc.id ?? index}-${doc.fileName}`}
                        fileName={doc.fileName ?? ""}
                        icon
                        documentId={doc.id}
                    />
                ))}
            </VStack>
        </ViewTableCell>
    );
};

export const PreviousDocumentListCell = memo(({ row }: CellProps) => (
    <DocumentListCell value={row.original.previousValue} />
));
PreviousDocumentListCell.displayName = "PreviousDocumentListCell";

export const ModifiedDocumentListCell = memo(({ row }: CellProps) => (
    <DocumentListCell value={row.original.modifiedValue} />
));
ModifiedDocumentListCell.displayName = "ModifiedDocumentListCell";

export const PreviousTextCell = memo(({ row }: CellProps) => (
    <ViewTableCell>
        <BreakableText
            text={toDisplayText(row.original.previous)}
            showTooltip
        />
    </ViewTableCell>
));
PreviousTextCell.displayName = "PreviousTextCell";

export const ModifiedTextCell = memo(({ row }: CellProps) => (
    <ViewTableCell>
        <BreakableText
            text={toDisplayText(row.original.modified)}
            showTooltip
        />
    </ViewTableCell>
));
ModifiedTextCell.displayName = "ModifiedTextCell";
